import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnswerGroup } from '../../types';
import { questionSummaryKey } from '@arnold/core';
import { ColumnType, type TableColumn } from './types';
import { ChoicesCell, CustomMetricCell, MetricCell, MissingValue, ResponseRateCell } from './cells/MetricCells';
import { TextCell, ThemeChips } from './cells/TextCell';
import { CellStack, Chip, Chips, Muted } from './styles';

const defaults: Record<ColumnType, { width: number; minWidth: number; maxWidth: number }> = {
  [ColumnType.CUSTOM_METRIC]: { width: 220, minWidth: 130, maxWidth: 900 },
  [ColumnType.QUESTION_ANSWER_COUNT]: { width: 120, minWidth: 85, maxWidth: 400 },
  [ColumnType.LABEL]: { width: 260, minWidth: 160, maxWidth: 1000 },
  [ColumnType.TEAM]: { width: 180, minWidth: 110, maxWidth: 800 },
  [ColumnType.RESPONDENT_COUNT]: { width: 120, minWidth: 85, maxWidth: 400 },
  [ColumnType.ANSWER_COUNT]: { width: 120, minWidth: 85, maxWidth: 400 },
  [ColumnType.RESPONSE_RATE]: { width: 180, minWidth: 130, maxWidth: 700 },
  [ColumnType.NPS]: { width: 250, minWidth: 160, maxWidth: 900 },
  [ColumnType.MINI_NPS]: { width: 190, minWidth: 140, maxWidth: 700 },
  [ColumnType.SCORE]: { width: 190, minWidth: 140, maxWidth: 700 },
  [ColumnType.CHOICES]: { width: 260, minWidth: 160, maxWidth: 900 },
  [ColumnType.TEXT]: { width: 420, minWidth: 200, maxWidth: 1500 },
  [ColumnType.THEMES]: { width: 220, minWidth: 130, maxWidth: 900 },
};

export const getColumnSizing = (column: TableColumn) => {
  const fallback = defaults[column.type];
  const requestedMax = Number.isFinite(column.maxWidth) ? Math.max(1, column.maxWidth!) : fallback.maxWidth;
  const minWidth = Number.isFinite(column.minWidth)
    ? Math.max(1, column.minWidth!)
    : Math.min(fallback.minWidth, requestedMax);
  const maxWidth = Math.max(minWidth, requestedMax);
  const width = Number.isFinite(column.width) ? column.width! : fallback.width;
  return { minWidth, maxWidth, width: Math.max(minWidth, Math.min(width, maxWidth)) };
};

function assertNever(column: never): never {
  throw new Error(`Unsupported AnswerGroup column: ${JSON.stringify(column)}`);
}

export const AnswerGroupCell = memo(({ group, column }: { group: AnswerGroup; column: TableColumn }) => {
  const { i18n, t } = useTranslation('newReport');
  const summary =
    'questionIndex' in column
      ? group.questionSummaries.get(questionSummaryKey(column.questionIndex, column.statementIndex))
      : undefined;
  switch (column.type) {
    case ColumnType.CUSTOM_METRIC:
      return (
        <CustomMetricCell
          summary={group.customMetrics[column.metricId]}
          presentation={column.presentation}
          format={column.format}
        />
      );
    case ColumnType.QUESTION_ANSWER_COUNT:
      return <span>{summary?.respondingChannelIds.size ?? 0}</span>;
    case ColumnType.LABEL:
      return (
        <CellStack>
          <span>{group.label}</span>
          {group.type === 'anonymous' && <Muted>{t('table.pooledResponses')}</Muted>}
        </CellStack>
      );
    case ColumnType.TEAM:
      return (
        <Chips>
          {group.teamNames.map((name) => (
            <Chip key={name}>{name}</Chip>
          ))}
        </Chips>
      );
    case ColumnType.RESPONDENT_COUNT:
      return <span>{new Intl.NumberFormat(i18n.language).format(group.respondentCount)}</span>;
    case ColumnType.ANSWER_COUNT:
      return <span>{new Intl.NumberFormat(i18n.language).format(group.answerCount)}</span>;
    case ColumnType.RESPONSE_RATE:
      return <ResponseRateCell value={group.metrics.responseRate} />;
    case ColumnType.NPS:
    case ColumnType.MINI_NPS:
    case ColumnType.SCORE:
      return <MetricCell summary={summary} variant={column.type} />;
    case ColumnType.CHOICES:
      return <ChoicesCell summary={summary} individual={group.type === 'individual'} />;
    case ColumnType.TEXT:
      return (
        <TextCell entries={(column.source === 'comment' ? summary?.commentEntries : summary?.textEntries) ?? []} />
      );
    case ColumnType.THEMES:
      return summary ? (
        <ThemeChips themes={summary.themes[column.source ?? 'text']} />
      ) : (
        <MissingValue reason="noThemes" />
      );
    default:
      return assertNever(column);
  }
});
